"use client"

import { useContext, useEffect, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import axios from "axios"
import { motion } from "framer-motion"
import { Mail, Lock, LogIn, AlertCircle, Ticket } from "lucide-react" 
import { UserContext } from "../../context/UserContext"
import "../../styles/circuitoDesignSystem.css"
import { DomainContext } from "./LayoutCircuito"
const API = process.env.REACT_APP_API_URL || "https://api.ticketdovaqueiro.com/api"

export default function CircuitoInscricaoAcesso() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user, loginUser } = useContext(UserContext)
  const { basePath } = useContext(DomainContext)
  const [email, setEmail] = useState("")
  const [senha, setSenha] = useState("")
  const [loading, setLoading] = useState(false)
  const [erro, setErro] = useState("")

  // 💡 RASTREADOR DE AFILIADOS
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCodigo = params.get("ref");
    if (refCodigo) {
      localStorage.setItem("afiliado_ref", refCodigo.toLowerCase().trim());
    }
  }, []);

  // 🔁 Se já estiver logado, pula esse step
  useEffect(() => {
    if (user) {
      navigate(`${basePath}/inscrever-se/dados`)
    }
  }, [user, slug, navigate])

  const handleLogin = async () => {
    if (!email || !senha) {
      setErro("Informe e-mail e senha.")
      return
    }

    try {
      setLoading(true)
      setErro("")

      const res = await axios.post(`${API}/auth/login`, { email, senha })
      // 💡 CORREÇÃO: salva o token no localStorage ANTES de chamar loginUser.
      // Isso garante que, quando o redirect disparar e CircuitoInscricaoDados montar,
      // o localStorage.getItem('token') já retorna o valor correto.
      localStorage.setItem("token", res.data.token)
      loginUser(res.data.user, res.data.token)

    } catch (e) {
      console.error("Erro no fluxo de login:", e)
      setErro(e?.response?.data?.mensagem || "E-mail ou senha inválidos.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="circuito-shell">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          maxWidth: 480,
          margin: "0 auto",
          padding: "40px 16px",
        }}
      >
        <div
          style={{
            background: "var(--c-card)",
            borderRadius: "var(--card-radius)",
            padding: "40px 32px",
            border: "var(--card-border)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
            position: "relative",
            overflow: "hidden"
          }}
        >
          {/* Efeito de luz de fundo */}
          <div style={{
            position: "absolute",
            top: "-50px", left: "-50px", width: "150px", height: "150px",
            background: "var(--c-accent)",
            filter: "blur(100px)", opacity: 0.15, pointerEvents: "none"
          }} />

          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <motion.div 
              initial={{ scale: 0.8 }} animate={{ scale: 1 }} transition={{ delay: 0.2 }}
              style={{ display: "inline-flex", background: "rgba(255,255,255,0.05)", padding: 16, borderRadius: "50%", marginBottom: 16 }}
            >
              <Ticket size={48} color="var(--c-accent)" strokeWidth={1.5} />
            </motion.div>
            <h1 style={{ fontSize: "28px", fontWeight: 800, marginBottom: 8 }}>
              Acesse sua <span style={{ color: "var(--c-accent)" }}>Conta</span>
            </h1>
          </div>

          <div style={{ 
            background: "rgba(255,255,255,0.03)", 
            border: "1px solid rgba(255,255,255,0.1)", 
            borderRadius: "12px", 
            padding: "16px", 
            marginBottom: "24px",
            textAlign: "center"
          }}>
            <p style={{ margin: 0, fontSize: "14px", color: "rgba(255,255,255,0.8)", lineHeight: 1.5 }}>
              🤠 <strong>Já usa o Ticket do Vaqueiro?</strong><br/>
              Use o seu mesmo e-mail e senha! Sua conta é única para comprar senhas nas etapas e se associar aos circuitos.
            </p>
          </div>

          {erro && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                marginBottom: 24,
                padding: "14px 16px",
                borderRadius: 12,
                background: "rgba(239,68,68,0.1)",
                border: "1px solid rgba(239,68,68,0.2)",
                color: "#f87171",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: 10
              }}
            >
              <AlertCircle size={20} />
              <span>{erro}</span>
            </motion.div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Input E-mail */}
            <div style={{ position: "relative" }}>
              <Mail size={20} style={{ position: "absolute", top: "14px", left: "16px", opacity: 0.5 }} />
              <input
                type="email"
                placeholder="E-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="c-input"
                style={{ paddingLeft: "48px", width: "100%", height: "48px" }}
              />
            </div>

            {/* Input Senha */}
            <div style={{ position: "relative" }}>
              <Lock size={20} style={{ position: "absolute", top: "14px", left: "16px", opacity: 0.5 }} />
              <input
                type="password"
                placeholder="Senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="c-input"
                style={{ paddingLeft: "48px", width: "100%", height: "48px" }}
              />
            </div>

            {/* 💡 NOVO: Link de Esqueci a Senha */}
            <div style={{ textAlign: "right", marginTop: "-8px" }}>
              <Link
                to={`${basePath}/recuperar-senha`}
                style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", textDecoration: "underline" }}
              >
                Esqueceu a senha?
              </Link>
            </div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleLogin}
            disabled={loading}
            className="c-btn c-btn--primary"
            style={{ 
              width: "100%", 
              marginTop: 24, 
              height: "52px", 
              fontSize: "16px", 
              fontWeight: 700,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8
            }}
          >
            {loading ? (
              <span style={{ opacity: 0.8 }}>Entrando...</span>
            ) : (
              <>
                <LogIn size={20} /> Entrar
              </>
            )}
          </motion.button>

          <div
            style={{
              marginTop: 24,
              textAlign: "center",
              fontSize: "14px",
              opacity: 0.8,
            }}
          >
            Não tem conta?{" "}
            <Link
              to={`${basePath}/inscrever-se/cadastro`}
              style={{
                color: "var(--c-accent)",
                fontWeight: 700,
                textDecoration: "none",
                marginLeft: 4
              }}
            >
              Criar agora
            </Link>
          </div>

          {/* Sutil "Powered By" no rodapé do card */}
          <div style={{ marginTop: "32px", textAlign: "center", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: "16px" }}>
             <span style={{ fontSize: "11px", color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "1px" }}>
               Tecnologia segura por <strong>Ticket do Vaqueiro</strong>
             </span>
          </div>

        </div>
      </motion.div>
    </div>
  )
}